/**
 * Tab-title flash when a job transitions to a terminal state while
 * the tab is unfocused. Zero-permission notification: works the
 * moment the SPA loads without any browser prompts.
 *
 * Watches the shared ``jobs`` ref from ``useJobs`` for DISPLAY-status
 * transitions ``processing → completed | failed`` (the events the
 * user actually cares about). Display status maps customer_done onto
 * "completed" (see utils/jobDisplay.ts), so the ✔ fires the moment
 * the preview is deliverable — not ~25–40s later at internal
 * completion. Aggregates counts separately for
 * completions vs failures so the title shows
 * ``(2) ✔ (1) ✗ Segment Web …`` not a single mixed count — failures
 * are typically more time-sensitive.
 *
 * Resets on the next ``visibilitychange → visible`` so returning to
 * the tab clears the unread state without the user clicking anything.
 */
import { onMounted, onUnmounted, watch } from "vue";
import { useJobs, type JobStatus, type JobSummary } from "./useJobs";
import { displayStatus } from "../utils/jobDisplay";

const BASE_TITLE = "Segment Web";

interface Counts {
  completed: number;
  failed: number;
}

/** Build a title string from current counts. Empty counts → just BASE_TITLE. */
function formatTitle(c: Counts): string {
  const parts: string[] = [];
  if (c.completed > 0) parts.push(`(${c.completed}) ✔`);
  if (c.failed > 0) parts.push(`(${c.failed}) ✗`);
  if (parts.length === 0) return BASE_TITLE;
  return `${parts.join(" ")} ${BASE_TITLE}`;
}

/**
 * Mount-once notifier. Returns nothing — registers lifecycle hooks
 * to attach + tear down listeners. Call from a component that lives
 * for the whole signed-in session (App.vue).
 *
 * The composable is module-scoped (no exposed state) because there's
 * only ever one tab title; multiple instances would race the
 * document.title write.
 */
export function useTabTitleNotifier(): void {
  const { jobs } = useJobs();

  const counts: Counts = { completed: 0, failed: 0 };
  // Cache the previous-tick DISPLAY status per job so we can detect
  // processing→done transitions without false positives on jobs
  // already terminal at first load. Tracking displayStatus (not raw
  // status) makes the title flip at customer_done — and because the
  // cached value is already "completed" from that tick on, the later
  // raw processing→completed terminal flip is not a fresh transition,
  // so it can't double-fire.
  const lastStatus = new Map<string, JobStatus>();

  function isHidden(): boolean {
    // Defensive against environments where document is mocked or
    // missing the visibility API (jsdom older versions). Treat
    // missing-state as visible (no flash) — the user wouldn't see
    // it anyway in those environments.
    if (typeof document === "undefined") return false;
    return document.visibilityState === "hidden";
  }

  function applyTitle() {
    if (typeof document !== "undefined") {
      document.title = formatTitle(counts);
    }
  }

  function reset() {
    counts.completed = 0;
    counts.failed = 0;
    applyTitle();
  }

  function onVisibilityChange() {
    if (!isHidden()) reset();
  }

  // Initial pass: seed lastStatus with each job's current status so
  // any job already terminal at first render doesn't count as a fresh
  // transition. Without this, opening the SPA to a list of done jobs
  // would immediately stamp "(5) ✔" on the tab title.
  function seedLastStatus(initial: JobSummary[]) {
    for (const j of initial) lastStatus.set(j.job_id, displayStatus(j));
  }

  // Per-tick reconciliation. Called on every jobs[] mutation.
  function reconcile(current: JobSummary[]) {
    let changed = false;
    for (const j of current) {
      const status = displayStatus(j);
      const prev = lastStatus.get(j.job_id);
      lastStatus.set(j.job_id, status);
      // Only fire on processing→done. If a job appeared for the
      // first time already done (e.g. polled after the user signed
      // in mid-completion), skip — the transition wasn't visible to
      // this session.
      if (prev === "processing" && status === "completed") {
        if (isHidden()) {
          counts.completed += 1;
          changed = true;
        }
      } else if (prev === "processing" && status === "failed") {
        if (isHidden()) {
          counts.failed += 1;
          changed = true;
        }
      }
    }
    if (changed) applyTitle();
  }

  onMounted(() => {
    seedLastStatus(jobs.value);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
  });

  onUnmounted(() => {
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      // Reset title so a remount doesn't pick up a stale counter
      // string left from this instance.
      document.title = BASE_TITLE;
    }
  });

  watch(
    jobs,
    (next) => {
      reconcile(next);
    },
    { deep: true },
  );
}
